"use client";

import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { downloadReport } from "@/lib/report-download";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { ArrowLeft } from "lucide-react";

export default function Gstr1Page() {
  const now = useMemo(() => new Date(), []);
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));

  const { data, refetch } = useQuery({
    queryKey: ["reports", "gstr1", month, year],
    queryFn: async () => {
      const { data: d } = await api.get("/api/reports/gstr1", {
        params: { month: Number(month), year: Number(year) },
      });
      return d as {
        b2b: Record<string, unknown>[];
        b2c_small: Record<string, unknown>[];
        b2cl: Record<string, unknown>[];
        hsn: Record<string, unknown>[];
      };
    },
  });

  return (
    <div className="space-y-6 p-6 max-w-6xl">
      <Link
        to="/reports"
        className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "gap-2 inline-flex")}
      >
        <ArrowLeft className="h-4 w-4" />
        Reports
      </Link>
      <h1 className="text-2xl font-semibold tracking-tight">GSTR-1</h1>
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-end gap-4 justify-between">
          <div className="flex flex-wrap gap-4">
            <div className="grid gap-1.5">
              <Label className="text-xs">Month</Label>
              <Input
                type="number"
                min={1}
                max={12}
                className="w-24"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Year</Label>
              <Input className="w-28" value={year} onChange={(e) => setYear(e.target.value)} />
            </div>
            <Button type="button" variant="secondary" className="mt-auto" onClick={() => void refetch()}>
              Load
            </Button>
          </div>
          <Button
            type="button"
            variant="outline"
            className="mt-auto"
            onClick={() =>
              void downloadReport(
                "/api/reports/gstr1/export",
                { month: Number(month), year: Number(year) },
                `gstr1-${year}-${month}.xlsx`,
              )
            }
          >
            Excel (GSTN layout)
          </Button>
        </CardHeader>
      </Card>

      <Tabs defaultValue="b2b">
        <TabsList>
          <TabsTrigger value="b2b">B2B</TabsTrigger>
          <TabsTrigger value="b2c">B2C Small</TabsTrigger>
          <TabsTrigger value="b2cl">B2CL</TabsTrigger>
          <TabsTrigger value="hsn">HSN</TabsTrigger>
        </TabsList>
        <TabsContent value="b2b">
          <JsonTable rows={data?.b2b ?? []} />
        </TabsContent>
        <TabsContent value="b2c">
          <JsonTable rows={data?.b2c_small ?? []} />
        </TabsContent>
        <TabsContent value="b2cl">
          <JsonTable rows={data?.b2cl ?? []} />
        </TabsContent>
        <TabsContent value="hsn">
          <JsonTable rows={data?.hsn ?? []} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function JsonTable({ rows }: { rows: Record<string, unknown>[] }) {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">No rows.</p>;
  }
  const keys = Object.keys(rows[0]);

  return (
    <Card className="mt-4">
      <CardContent className="pt-4 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b">
              {keys.map((k) => (
                <th key={k} className="p-2 text-left font-medium">
                  {k}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t">
                {keys.map((k) => (
                  <td key={k} className="p-2">
                    {String(r[k] ?? "")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
